import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const OrderList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1');

  const [data, setData] = useState({
    orders: [],
    order_items_map: {},
    order_delivery_map: {},
    total_pages: 0,
    page: 1,
    start_page: 1,
    end_page: 1
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/orders/list?page=${currentPage}`);
        setData(res.data);
      } catch (err) {
        console.error("주문 목록 로드 실패", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [currentPage]);

  const getDeliveryMapItem = (orderId) => {
    return data.order_delivery_map?.[orderId] || data.order_delivery_map?.[String(orderId)] || {};
  };

  const getOrderItems = (orderId) => {
    return data.order_items_map?.[orderId] || data.order_items_map?.[String(orderId)] || [];
  };

  const getDeliveryStatusText = (order) => {
    const delivery = getDeliveryMapItem(order.id);
    const delivery_status = delivery.delivery_status || '';
    const is_cancelled = order.status === '주문취소';

    if (is_cancelled) return '주문취소';
    if (delivery_status === '배송완료') return '배송완료';
    if (delivery_status === '배송중') return '배송중';
    if (delivery_status === '배송준비중') return '배송준비중';
    return '결제완료';
  };

  if (loading) return <div className="container mt-5 text-center">로딩 중...</div>;

  return (
    <div className="container my-2 px-0" style={{ maxWidth: '600px', paddingTop: '20px' }}>
      <h2 className="mb-3 mx-3">주문 내역</h2>

      {/* ✅ 주문 없음 처리 */}
      {!data.orders || data.orders.length === 0 ? (
        <div
          className="alert text-secondary mx-2"
          style={{ background: '#f1f3f5', fontSize: '13px' }}
        >
          주문 내역이 없습니다.
        </div>
      ) : (
        data.orders.map((order) => {
          const orderId = order.id;
          const items = getOrderItems(orderId);

          return (
            <div key={orderId} className="order-card" style={styles.orderCard}>
              <div className="order-header" style={styles.orderHeader}>
                <div>
                  <div className="order-number" style={styles.orderNumber}>
                    주문번호: {orderId}
                  </div>
                  <div className="order-info order-date" style={styles.orderDate}>
                    주문일자: {order.order_date}
                  </div>
                </div>

                <div className="delivery-section" style={styles.deliverySection}>
                  <div className="order-info order-date" style={styles.orderDate}>
                    {getDeliveryStatusText(order)}
                  </div>
                </div>
              </div>

              <div className="product-list">
                {items.map((item, idx) => (
                  <div key={idx} className="product-item" style={styles.productItem}>
                    <div className="product-thumb" style={styles.productThumb}>
                      <img
                        src={item.image_path?.startsWith('http') ? item.image_path : `/static/${item.image_path}`}
                        alt="상품 이미지"
                        style={styles.productThumbImg}
                      />
                    </div>
                    <div className="product-info">
                      <div className="product-name" style={styles.productName}>{item.name}</div>
                      <div className="product-qty" style={styles.productQty}>수량: {item.quantity}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="order-actions" style={styles.orderActions}>
                <button
                  className="order-detail-link"
                  style={styles.orderDetailLink}
                  onClick={() => navigate(`/order/detail?order_id=${orderId}`)}
                >
                  주문상세보기
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* ✅ pagination도 주문 있을 때만 */}
      {data.total_pages > 1 && data.orders.length > 0 && (
        <nav className="mt-3">
          <ul className="pagination justify-content-center" style={styles.pagination}>
            {data.start_page > 1 && (
              <li className="page-item">
                <button
                  className="page-link"
                  onClick={() => navigate(`/order/list?page=${data.start_page - 1}`)}
                >
                  &laquo;
                </button>
              </li>
            )}

            {Array.from(
              { length: data.end_page - data.start_page + 1 },
              (_, i) => {
                const pageNum = data.start_page + i;
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${pageNum === data.page ? 'active' : ''}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => navigate(`/order/list?page=${pageNum}`)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              }
            )}

            {data.end_page < data.total_pages && (
              <li className="page-item">
                <button
                  className="page-link"
                  onClick={() => navigate(`/order/list?page=${data.end_page + 1}`)}
                >
                  &raquo;
                </button>
              </li>
            )}
          </ul>
        </nav>
      )}
    </div>
  );
};

const styles = {
  container: { width: '100%', maxWidth: '600px', margin: '0 auto', padding: '0 10px 40px', boxSizing: 'border-box' },
  pageTitle: { marginTop: '20px', fontWeight: 'bold' },
  orderCard: { background: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', marginBottom: '28px', padding: '20px' },
  orderHeader: { borderBottom: '1px solid #eee', paddingBottom: '12px', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  orderNumber: { fontWeight: '700', fontSize: '1.1rem' },
  orderDate: { fontSize: '0.75rem', color: '#888' },
  deliverySection: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' },
  productItem: { display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', padding: '12px 0' },
  productThumb: { width: '60px', height: '60px', overflow: 'hidden', marginRight: '15px', flexShrink: 0, background: '#eee', border: '0px solid #ddd', borderRadius: '4px' },
  productThumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  productName: { fontWeight: '600', fontSize: '1rem', marginBottom: '4px' },
  productQty: { fontSize: '0.85rem', color: '#888' },
  orderActions: { marginTop: '10px', textAlign: 'right' },
  orderDetailLink: { background: 'none', border: 'none', fontSize: '0.9rem', color: '#212529', textDecoration: 'none', fontWeight: '600', cursor: 'pointer' },
  pagination: { display: 'flex', justifyContent: 'center', listStyle: 'none', padding: 0 }
};

export default OrderList;